import {
  Button,
  CardBackground,
  DialogClose,
  Typography,
} from "@hellocarbo/lasagne";
import { PieChartRef } from "@ui/PieChart";
import { Diff, FileDiff, Upload } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import invariant from "tiny-invariant";

import { CategoryStyles } from "@/config";
import useOutboundQueryParams from "@/hooks/useOutboundQueryParams";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import { Policy, Review, ReviewCategory } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";

import ReviewCard from "./ReviewCard";
import ReviewPie from "./ReviewPie";

function CarbonEmissionPanel({
  data,
  comparisonPolicy,
}: {
  data: Review;
  comparisonPolicy: Policy;
}) {
  const { reviewId, admin, slug } = useParams();
  const { t } = useTranslation([
    "common",
    "formatter",
    "review",
    "reviewCategories",
  ]);
  const params = useOutboundQueryParams("upgrade");
  const { sendSegmentEvent } = useSegmentEvent();

  const chartRef = useRef<PieChartRef>(null);

  const {
    review_categories: reviewCategoriesData,
    carbon,
    show_comparison: isComparisonAvailable,
    hide_public_comparison: hidePublicComparison,
    is_external: isExternal,
  } = data;

  invariant(reviewId || slug, "Review id and slug are missing.");

  const isPublicReview = !reviewId && slug;

  const canSeeComparison = comparisonPolicy.queries["index"];

  const showComparison = isPublicReview
    ? !hidePublicComparison && isComparisonAvailable && canSeeComparison // allows a user to see the comparison when not logged in (public review)
    : isComparisonAvailable;

  const handleChartClick = (key: string | undefined) => {
    const reviewCategoryId = reviewCategoriesData.find(
      ({ key: reviewCategoryKey }) => reviewCategoryKey === key
    )?.id;
    if (!reviewCategoryId) {
      return;
    }

    const linkUrl = isPublicReview
      ? `/reviews/${slug}/public/${reviewCategoryId}`
      : `/reviews/${reviewId}/review_categories/${reviewCategoryId}${admin ? "/admin" : ""}`;

    window.location.assign(linkUrl);
  };

  const { formattedCarbon: formattedCarbonTotal, unit: totalUnit } =
    formatCarbon(t, carbon);

  const reviewCategoriesUnit = getCarbonDisplayUnit(
    reviewCategoriesData.map((reviewCategory) => reviewCategory.carbon)
  );

  return (
    <CardBackground>
      <Typography step="2" weight="bold" as="h2" className="mb-sm">
        {t("review:carbonEmissions.title")}
      </Typography>
      <div className="gap-md 2xl:gap-xl grid grid-cols-1 2xl:grid-cols-[1fr_3fr]">
        <div className="min-h-[225px]">
          <ReviewPie
            data={reviewCategoriesData}
            title={formattedCarbonTotal}
            titleSubText={t(`common:unit.${totalUnit}`)}
            chartRef={chartRef}
            onClick={handleChartClick}
          />
        </div>
        <ul className="gap-md grid grid-cols-1 xl:grid-cols-2">
          {reviewCategoriesData
            .filter((category: ReviewCategory) => !!category.carbon)
            .map((category: ReviewCategory) => {
              const { formattedCarbon } = formatCarbon(
                t,
                category.carbon,
                reviewCategoriesUnit
              );

              const { Icon } = CategoryStyles[category.key];

              return (
                <li key={category.key}>
                  <ReviewCard
                    url={
                      isPublicReview
                        ? `/reviews/${slug}/public/${category.id}`
                        : `/reviews/${reviewId}/review_categories/${category.id}${
                            admin ? "/admin" : ""
                          }`
                    }
                    Icon={Icon}
                    title={t(`reviewCategories:${category.key}`)}
                    subtitle={`${formattedCarbon} ${t(`common:unit.${reviewCategoriesUnit}`)}`}
                  />
                </li>
              );
            })}
          {showComparison && (
            <li key="sectoralComparison">
              <ReviewCard
                title={t("review:comparison.sectoralComparison")}
                url={
                  isPublicReview
                    ? `/reviews/${slug}/public/comparison`
                    : `/reviews/${reviewId}/comparison${admin ? "/admin" : ""}`
                }
                Icon={Diff}
                buttonIntent="secondary"
                upsellModal={{
                  title: t("review:comparison.upsellModal.title"),
                  description: t("review:comparison.upsellModal.description"),
                  warningMessage: t("review:comparison.upsellModal.warning"),
                  show: !canSeeComparison,
                  footer: (
                    <DialogClose asChild>
                      <Button
                        as="a"
                        href={`https://try.hellocarbo.com/meetings/emmanuel-watrinet/rdv-abonnement-carbo-entreprise${params}`}
                        shape="solid"
                        onClick={() => sendSegmentEvent("clic_CTA_upsell")}
                        shouldOpenNewTab
                      >
                        {t("review:comparison.upsellModal.cta")}
                      </Button>
                    </DialogClose>
                  ),
                }}
              />
            </li>
          )}
          {!isPublicReview && (
            <li key="exports">
              <ReviewCard
                title={t("review:exports.title")}
                url={`/reviews/${reviewId}/exports`}
                Icon={Upload}
                buttonIntent="secondary"
              />
            </li>
          )}
          {!isExternal && !isPublicReview && (
            <li key="internalComparison">
              <ReviewCard
                title={t("review:comparison.internalComparison")}
                url={`/reviews/${reviewId}/internal_comparison`}
                Icon={FileDiff}
                buttonIntent="secondary"
              />
            </li>
          )}
        </ul>
      </div>
    </CardBackground>
  );
}

export default CarbonEmissionPanel;
